import { useEffect, useRef } from 'react';
import { SESION_INACTIVIDAD_MINUTOS, type TipoDePagina } from '@ahora-patagonia/contracts';
import { emitir } from './senales.js';

/**
 * Que se vio una página: la señal `pagina_vista`, de la que salen "Notas abiertas", "Visitas al
 * sitio", secciones, provincias y dispositivos del tablero.
 *
 * **Faltó desde que salió el módulo.** La ingesta, el volcado y el tablero estaban, pero ninguna
 * pantalla emitía esta señal, y como la API contesta 204 a todo, el tablero mostró ceros durante
 * semanas sin un solo error en ningún log. Por eso cada vista del sitio la llama explícitamente y
 * `ssr-routes.spec` falla si una ruta deja de hacerlo.
 *
 * `vista` es **qué** se está mirando, o `null` mientras no hay datos: el id de la nota, el de la
 * sección, el tipo de listado. Se cuenta una vista cada vez que cambia, y no en cada render ni en
 * cada carga de datos, por tres casos que lo exigen:
 *
 *  - De una nota a otra la vista no se remonta (misma ruta): el id cambia y cuenta.
 *  - "Ver más" en una sección reemplaza el objeto de datos pero no la sección: no cuenta.
 *  - En el render justo después de navegar los datos todavía son los de la página anterior, y la
 *    clave es la misma que ya se contó: no se cuenta la página vieja con la URL nueva.
 *  - Mientras carga, `vista` pasa por `null`. Se compara contra **la última contada** y no contra
 *    el render anterior: sin eso, `ultima-hora → (null) → ultima-hora` contaría dos veces.
 *
 * Volver a la misma página remontando la vista (nota → portada → la misma nota) cuenta de nuevo,
 * que es lo que una vista es.
 */
export function useSenalDePagina(
  pagina: TipoDePagina,
  vista: string | null | undefined,
  entidadId?: string | null,
): void {
  const ultimaContada = useRef<string | null>(null);

  useEffect(() => {
    if (vista === null || vista === undefined) return;
    if (vista === ultimaContada.current) return;
    if (typeof window === 'undefined') return;

    // Se marca al emitir y no al entrar: en StrictMode el efecto se monta, se limpia y se vuelve a
    // montar, y marcar antes dejaba al prerender sin listener y sin vista.
    const contar = () => {
      ultimaContada.current = vista;
      emitir({ tipo: 'pagina_vista', pagina, entidadId: entidadId ?? null, abreVisita: abreVisita() });
    };

    // El prerender de Chrome arma la página en una pestaña que nadie mira, y `emitir` descarta lo
    // que llega en ese estado. La lectura se recupera sola porque su reloj arranca de nuevo, pero
    // esta señal sale una sola vez: se espera a que la pestaña se active de verdad.
    const doc = document as Document & { prerendering?: boolean };
    if (doc.prerendering === true) {
      document.addEventListener('prerenderingchange', contar, { once: true });
      return () => document.removeEventListener('prerenderingchange', contar);
    }
    contar();
    return undefined;
    // `entidadId` va con `vista`: cambia solo cuando cambia lo que se mira.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, vista]);
}

const CLAVE = 'senales-ultima-vista';

/**
 * Si esta vista abre una visita: la anterior fue hace más de `SESION_INACTIVIDAD_MINUTOS`, o no
 * hubo. **Es solo lo que cree el navegador**; quien decide es la marca de sesión del servidor, así
 * que un almacenamiento bloqueado o borrado no cambia ningún número.
 */
function abreVisita(): boolean {
  const ahora = Date.now();
  try {
    const anterior = Number(window.localStorage.getItem(CLAVE));
    window.localStorage.setItem(CLAVE, String(ahora));
    return !anterior || ahora - anterior > SESION_INACTIVIDAD_MINUTOS * 60_000;
  } catch {
    return true;
  }
}
